/**
 * Dropdown disegnato da noi al posto del modale nativo del select continenti.
 *
 * Architettura obbligatoria: il <select> che init() riceve RESTA nel DOM ed è
 * la sorgente di verità. Questo modulo gli costruisce sopra un pulsante e un
 * pannello di opzioni (pattern ARIA "listbox"), lo nasconde visivamente e
 * dall'ordine di tabulazione, e ad ogni scelta scrive il valore nel select
 * nativo e fa scattare il suo evento 'change'. Il chiamante (factbook-map.js)
 * continua ad ascoltare quel 'change' senza sapere nulla di questo
 * componente. Senza JavaScript l'attributo `hidden` non viene mai applicato:
 * il select nativo resta l'unico controllo, visibile e funzionante.
 */

let uid = 0;

function nextId(prefix) {
  uid += 1;
  return `${prefix}-${uid}`;
}

/**
 * Inizializza il dropdown personalizzato sopra un <select> esistente.
 * Ritorna null se l'elemento passato non è un select: il chiamante può
 * ignorare l'esito senza controlli aggiuntivi, come già fa con `list` e
 * `search` altrove in factbook-map.js.
 */
export function initContinentDropdown(select) {
  if (!select || select.tagName !== 'SELECT') {
    return null;
  }

  const wrapper = document.createElement('div');
  wrapper.className = 'factbook-dropdown';

  const button = document.createElement('button');
  button.type = 'button';
  button.className = 'factbook-dropdown__button';
  button.id = nextId('factbook-dropdown-button');
  button.setAttribute('aria-haspopup', 'listbox');
  button.setAttribute('aria-expanded', 'false');

  const buttonLabel = document.createElement('span');
  buttonLabel.className = 'factbook-dropdown__label';
  button.appendChild(buttonLabel);

  const arrow = document.createElement('span');
  arrow.className = 'factbook-dropdown__arrow';
  arrow.setAttribute('aria-hidden', 'true');
  button.appendChild(arrow);

  const panel = document.createElement('ul');
  panel.className = 'factbook-dropdown__panel';
  panel.id = nextId('factbook-dropdown-panel');
  panel.setAttribute('role', 'listbox');
  panel.hidden = true;

  button.setAttribute('aria-controls', panel.id);

  // L'etichetta esistente (screen-reader-text, collegata al select nativo
  // tramite "for") resta il nome accessibile del filtro: il pulsante la
  // referenzia insieme a se stesso, così chi legge lo schermo sente
  // "Filtra per continente, Tutti i continenti" invece del solo valore
  // corrente.
  const existingLabel = select.id ? document.querySelector(`label[for="${select.id}"]`) : null;

  if (existingLabel) {
    if (!existingLabel.id) {
      existingLabel.id = nextId('factbook-dropdown-label');
    }

    button.setAttribute('aria-labelledby', `${existingLabel.id} ${button.id}`);
    panel.setAttribute('aria-labelledby', existingLabel.id);
  }

  const options = Array.from(select.options).map((option, index) => {
    const item = document.createElement('li');
    item.className = 'factbook-dropdown__option';
    item.id = `${panel.id}-option-${index}`;
    item.setAttribute('role', 'option');
    item.dataset.value = option.value;
    item.textContent = option.textContent;
    item.tabIndex = -1;

    panel.appendChild(item);

    return item;
  });

  function optionForValue(value) {
    return options.find((item) => item.dataset.value === value) || null;
  }

  /**
   * Riallinea pulsante e opzioni al valore corrente del select nativo: serve
   * sia all'inizializzazione sia dopo ogni scelta, perché il select resta
   * l'unica fonte di verità sul valore attivo.
   */
  function syncFromSelect() {
    const current = select.options[select.selectedIndex];
    buttonLabel.textContent = current ? current.textContent : '';

    options.forEach((item) => {
      const isSelected = item.dataset.value === select.value;
      item.setAttribute('aria-selected', isSelected ? 'true' : 'false');
      item.classList.toggle('is-selected', isSelected);
      // Roving tabindex: quando qualcuno tabba dentro (o fuori) dal pannello,
      // solo la voce selezionata resta un fermo naturale; dentro il pannello
      // aperto la navigazione reale è a frecce, non a Tab.
      item.tabIndex = isSelected ? 0 : -1;
    });
  }

  syncFromSelect();

  let isOpen = false;

  // Dichiarate come `function` e non `const () =>`: vengono chiamate da
  // `openPanel()` qui sotto prima di essere lette nel file, e la hoisting
  // delle dichiarazioni di funzione (a differenza delle const) lo permette.
  function handleOutsideClick(event) {
    if (!wrapper.contains(event.target)) {
      closePanel(false);
    }
  }

  function handlePanelFocusOut(event) {
    const next = event.relatedTarget;

    // Il fuoco che esce dal pannello verso qualcosa fuori dal componente (Tab
    // per proseguire nella pagina) deve chiudere il pannello: altrimenti
    // resterebbe aperto e visibile mentre l'attenzione e' altrove.
    if (!next || !wrapper.contains(next)) {
      closePanel(false);
    }
  }

  function openPanel() {
    if (isOpen) {
      return;
    }

    isOpen = true;
    panel.hidden = false;
    button.setAttribute('aria-expanded', 'true');

    // Aprendo, il fuoco va sull'opzione già selezionata (specifica), non
    // sulla prima della lista.
    const target = optionForValue(select.value) || options[0];

    if (target) {
      target.focus();
    }

    // Fase di cattura: cosi' il click sul pulsante che ha appena aperto il
    // pannello non arriva qui PRIMA del listener 'click' del pulsante stesso,
    // che altrimenti si troverebbe il pannello gia' richiuso e lo riaprirebbe
    // subito dopo (toggle che non si accorge mai di essere stato chiuso).
    document.addEventListener('click', handleOutsideClick, true);
  }

  function closePanel(returnFocusToButton) {
    if (!isOpen) {
      return;
    }

    // isOpen a false PRIMA di nascondere il pannello: il blur che consegue a
    // `panel.hidden = true` (o al focus() sul pulsante qui sotto) fa scattare
    // handlePanelFocusOut, che altrimenti richiamerebbe closePanel() mentre è
    // già in corso.
    isOpen = false;
    panel.hidden = true;
    button.setAttribute('aria-expanded', 'false');
    document.removeEventListener('click', handleOutsideClick, true);

    if (returnFocusToButton) {
      button.focus();
    }
  }

  function chooseValue(value) {
    if (select.value !== value) {
      select.value = value;
      // Il ponte con factbook-map.js: quel listener ascolta 'change' sul
      // select nativo e non sa nulla di questo componente. `bubbles: true`
      // allinea l'evento sintetico a quello che il browser genera davvero.
      select.dispatchEvent(new Event('change', { bubbles: true }));
    }

    syncFromSelect();
    closePanel(true);
  }

  button.addEventListener('click', () => {
    if (isOpen) {
      closePanel(true);
    } else {
      openPanel();
    }
  });

  function moveFocus(delta) {
    const from = options.indexOf(document.activeElement);
    const start = from === -1 ? 0 : from;
    const next = Math.min(options.length - 1, Math.max(0, start + delta));

    options[next].focus();
  }

  panel.addEventListener('keydown', (event) => {
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        moveFocus(1);
        break;
      case 'ArrowUp':
        event.preventDefault();
        moveFocus(-1);
        break;
      case 'Home':
        event.preventDefault();
        options[0].focus();
        break;
      case 'End':
        event.preventDefault();
        options[options.length - 1].focus();
        break;
      case 'Enter':
      case ' ':
        event.preventDefault();

        if (document.activeElement && document.activeElement.dataset) {
          chooseValue(document.activeElement.dataset.value);
        }

        break;
      case 'Escape':
        event.preventDefault();
        closePanel(true);
        break;
      default:
        break;
    }
  });

  panel.addEventListener('focusout', handlePanelFocusOut);

  Array.from(options).forEach((item) => {
    item.addEventListener('click', () => {
      chooseValue(item.dataset.value);
    });
  });

  // Il select nativo resta nel DOM come sorgente di verità ma sparisce dalla
  // vista e dall'ordine di tabulazione: `hidden` lo toglie anche
  // dall'accessibility tree, così chi usa uno screen reader non incontra due
  // controlli per lo stesso filtro.
  select.hidden = true;

  select.insertAdjacentElement('afterend', wrapper);
  wrapper.appendChild(button);
  wrapper.appendChild(panel);

  return { syncFromSelect, closePanel };
}
